import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { EntityState, EntityType, EntityTypeEnum, ReferenceDataManagementEvent, TradingControlLimit, WorkflowActions } from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import { OrganisationSelectboxField } from 'view/shared/entity'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { createAllEntitiesSelector } from 'selectors/entitySelectors'
import { ClonedComponentByShowModal } from 'view/shared/modal'
import { createReferenceDataEntityHook, useReferenceDataAvailableEvents } from 'view/hooks'

export interface TradingControlDialogProps extends ClonedComponentByShowModal {
  workflowId: string
  entityId: string
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  min-height: 250px;
  overflow-y: auto;
  width: 450px;
`

const useReferenceDataTradingControlLimit = createReferenceDataEntityHook(EntityType.TRADING_CONTROL_LIMIT)
const existingTradingControlLimitsSelector = createAllEntitiesSelector(EntityType.TRADING_CONTROL_LIMIT)

export const TradingControlDialog: React.FC<TradingControlDialogProps> = ({ workflowId, entityId, onClose }) => {
  const dispatch = useDispatch()
  const userPreference = useSelector(userPreferenceSelector)
  const availableEvents = useReferenceDataAvailableEvents(workflowId)
  const tradingControlLimitEntity = useReferenceDataTradingControlLimit({ entityId, workflowId })
  const existingTradingControlLimits = useSelector(existingTradingControlLimitsSelector)

  const tradingControlLimit = tradingControlLimitEntity?.data

  const title = Locale.translate(
    tradingControlLimitEntity?.entityState === EntityState.NEW
      ? Translation.TC_NEW_TRADE_CONTROL_LIMIT
      : Translation.TC_EDIT_TRADE_CONTROL_LIMIT
  )

  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const hasValidationIssues = !!tradingControlLimitEntity?.validationIssues && tradingControlLimitEntity.validationIssues.length > 0

  const updateHandler = <K extends keyof TradingControlLimit>(key: K) => (value: TradingControlLimit[K]) => {
    if (!tradingControlLimit) return

    dispatch(
      WorkflowActions.createEntityUpdateReferenceData<TradingControlLimit>({
        entityId: tradingControlLimit.id,
        entityType: EntityTypeEnum.TRADING_CONTROL_LIMIT,
        updatedEntity: {
          ...tradingControlLimit,
          [key]: value
        },
        workflowId
      })
    )
  }

  const onSave = () => {
    dispatch(WorkflowActions.createSubmitWorkflow(workflowId))

    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    dispatch(WorkflowActions.createCancelWorkflow(workflowId))

    if (onClose) {
      onClose()
    }
  }

  const excludedOptionKeys = useMemo(() => existingTradingControlLimits.map(tcl => tcl.organisationId), [existingTradingControlLimits])

  if (!tradingControlLimitEntity || !tradingControlLimit) {
    return null
  }

  return (
    <ModalWindow title={title}>
      <Content>
        <OrganisationSelectboxField
          dataTestId={'TradingControlDialog-OrganisationSelectboxField-Organisation'}
          label={Locale.translate(Translation.SC_ORGANISATION)}
          placeholder={Locale.translate(Translation.SC_ORGANISATION_PLACEHOLDER)}
          value={tradingControlLimit.organisationId}
          readOnly={readOnly || tradingControlLimitEntity.entityState === EntityState.EXISTING}
          onUpdate={updateHandler('organisationId')}
          validationIssues={tradingControlLimitEntity.validationIssues || undefined}
          fieldName={'organisation'}
          excludeOptionKeys={excludedOptionKeys}
        />
        <NumericInputField
          label={Locale.translate(Translation.TC_LIMIT, { amountFormat: userPreference.amountFormat })}
          readOnly={readOnly}
          min={1}
          formatOptions={getNotionalFormatOptions(userPreference.amountFormat)}
          onUpdate={updateHandler('limit')}
          value={tradingControlLimit.limit}
          dataTestId={'TradingControlDialog-NumericInputField-Limit'}
          fieldName={'limit'}
          validationIssues={tradingControlLimitEntity.validationIssues || undefined}
        />
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'TradingControlDialog-TextButton-CancelButton'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'TradingControlDialog-TextButton-SaveButton'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
